import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "react-toastify/dist/ReactToastify.css";
import CreateVideoModal from "../AiModule/CreateVideoModal";
import { useSelector } from "react-redux";
import services from "../../API";
import { Loading } from "../Loader/Loading";
import { CopyPlus } from "lucide-react";
import ConfirmModal from "../Modals/ConfirmModal";

const Brands = () => {
  const navigate = useNavigate();
  const brands = useSelector((state) => state.data.apiData.brands?.data || []);
  const loading = useSelector((state) => state.data.loading.brands);

  const [searchTerm, setSearchTerm] = useState("");
  const [error, ] = useState("");
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [selectedContentFilter, ] = useState("ads");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedsBrandId, setSelectedBrandId] = useState("");
  const [selectedsBrandName, setSelectedBrandName] = useState("");
  const [deleteStatus, setDeleteStatus] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  useEffect(() => {
    services.getBrands();
  }, [deleteStatus]);

  const industryIcons = {
    Technology: "⚡",
    "Food & Beverage": "☕",
    "Health & Fitness": "❤️",
    "Fashion & Retail": "🎨",
    Gaming: "🎮",
    Other: "🏢",
  };
  const filteredBrands = brands?.filter(
    (brand) =>
      brand.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      brand.description?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      brand.industry?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // const handleDeleteBrand = (brandId) => {
  //   setBrands((prev) => prev.filter((brand) => brand.id !== brandId));
  // };

  const getIndustryIcon = (industry) => {
    return industryIcons[industry] || industryIcons.Other;
  };

  const handleGenerateContent = (brand) => {
    setSelectedBrandId(brand._id);
    setSelectedBrandName(brand.brandName);
    setShowGenerateModal(true);
    // Navigate to home page with this brand pre-selected and open generate modal
    // navigate("/app/ads?tab=generate&brand=" + brand.id);
  };

  const handleDeleteBrand = async () => {
    const response = await services.deleteBrand(brandToDelete._id);
    if ((response?.statusCode === 200 || response?.statusCode === "200")) {
      setDeleteStatus(!deleteStatus);
      setShowDeleteModal(false);
    }
  };

  const handleDuplicateBrand = async (brand) => {
    const payload = { brandId: brand._id };
    const response = await services.duplicateBrand(payload);
    if ((response?.statusCode === 201 || response?.statusCode === "201")) {
      services.getBrands();
    }
  };

  const handleEditBrand = (brand) => {
    navigate(`/app/brands/${brand._id}`);
  };

  const handleDropdownToggle = (e, brandId) => {
    e.stopPropagation();
    setOpenDropdownId(openDropdownId === brandId ? null : brandId);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      setOpenDropdownId(null);
    };
    if (openDropdownId !== null) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [openDropdownId]);

  return (
    <div className="responsive-page responsive-page--brands min-h-screen bg-white dark:bg-[#0A0A0A]">
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Warning"
        message={
          <>
            <strong>Are you sure you want to permanently delete this brand?</strong>
            <br />
            <br />
            This will delete this brand and all content associated with it, including any AI CMO project.
          </>
        }
        mode="confirm"
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleDeleteBrand()}
      />
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-[#fff]">
              My Brands
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your brand profiles and identity
            </p>
          </div>
          {brands.length > 0 && (
            <button
              style={{ backgroundColor: "#ff6600" }}
              className="hover:opacity-90 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              onClick={() => navigate("/app/brands/new")}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add New Brand
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search brands..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
            />
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {filteredBrands.length} brand
            {filteredBrands.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Brands Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {!loading &&
            filteredBrands?.map((brand) => (
              <div
                key={brand._id}
                className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/app/brands/${brand._id}`)}
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-2xl">
                        {getIndustryIcon(brand.industry)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-[#fff] text-md">
                          {brand?.internalName}
                        </h3>
                        <h3 className="font-semibold text-[#2564eb] dark:text-blue-400 text-xs">
                          Brand Name: {brand.brandName}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                          {brand.description}
                        </p>
                      </div>
                    </div>
                    <div className="relative">
                      <button
                        className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800 rounded flex items-center justify-center"
                        onClick={(e) => handleDropdownToggle(e, brand._id)}
                      >
                        <svg
                          className="h-4 w-4 text-gray-600 dark:text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                          />
                        </svg>
                      </button>
                      {openDropdownId === brand._id && (
                        <div className="absolute right-0 mt-2 w-44 bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1">
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditBrand(brand);
                              setOpenDropdownId(null);
                            }}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536M9 11l6 6M3 17v4h4l10.293-10.293a1 1 0 000-1.414l-3.586-3.586a1 1 0 00-1.414 0L3 17z"
                              />
                            </svg>
                            Edit
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateBrand(brand);
                              setOpenDropdownId(null);
                            }}
                          >
                            <CopyPlus className="w-4 h-4" />
                            Duplicate
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBrandToDelete(brand);
                              setShowDeleteModal(true);
                              setOpenDropdownId(null);
                            }}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                            Delete
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hidden"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateContent(brand);
                              setOpenDropdownId(null);
                            }}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3"
                              />
                            </svg>
                            Generate Content
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Brand Details */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                        {brand.industry}
                      </span>
                      <div className="flex gap-1">
                        {brand.brandColors.map((color, index) => (
                          <div
                            key={index}
                            className="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {brand.tone}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <span>Used {brand.numberOfUsed} times</span>
                    <span>
                      Created {moment(brand?.createdAt).format("MMM D, YYYY")}
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Empty State */}
        {filteredBrands.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <div className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4 text-4xl">
              🏢
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-[#fff] mb-2">
              No brands found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search terms
            </p>
          </div>
        )}

        {/* Empty State - No brands */}
        {loading ? (
          <Loading />
        ) : (
          brands.length === 0 && (
            <div className="text-center py-12">
              <div className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4 text-4xl">
                🏢
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-[#fff] mb-2">
                No brands yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create your first brand to get started
              </p>
              <button
                style={{ backgroundColor: "#ff6600" }}
                className="hover:opacity-90 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
                onClick={() => navigate("/app/brands/new")}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add New Brand
              </button>
            </div>
          )
        )}

        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-4 py-2 rounded mb-4">
            {error}
          </div>
        )}
      </div>
      {selectedsBrandId}
      {selectedsBrandName}
      {/* Generate Content Modal */}
      <CreateVideoModal
        open={showGenerateModal}
        onOpenChange={setShowGenerateModal}
        selectedBrandId={selectedsBrandId}
        selectedBrandName={selectedsBrandName}
        selectedAudienceId=""
        selectedAudienceName=""
        preselectedContentType={selectedContentFilter}
      />
    </div>
  );
};

export default Brands;
